import { useState } from 'react';
import { Button, Chip, IconButton, Stack, TextField, MenuItem, Tooltip, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from '@mui/material';
import { Add, Payments, Delete, Link as LinkIcon, UploadFile } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { AmFilterBar, type AmFilters } from '../../components/AmFilterBar';
import { BulkImportDialog } from '../../components/BulkImportDialog';
import { RequestPaymentDialog } from '../../components/RequestPaymentDialog';
import { amBulkApi } from '../../api/amBulk.api';
import { amRentApi, type AmRent, type CreateRentInput } from '../../api/amRent.api';
import { amStationsApi } from '../../api/amStations.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;
const firstOfMonth = () => new Date().toISOString().slice(0, 8) + '01';
const statusColor = (s: string): 'success' | 'warning' | 'error' | 'default' => (s === 'paid' ? 'success' : s === 'overdue' ? 'error' : 'warning');

export function RentPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [filters, setFilters] = useState<AmFilters>({});
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<CreateRentInput>({ stationId: 0, monthYear: firstOfMonth(), amount: 0, dueDate: firstOfMonth() });
  const [requestRow, setRequestRow] = useState<AmRent | null>(null);
  const [deleting, setDeleting] = useState<AmRent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['am-rent', page, limit, filters],
    queryFn: () => amRentApi.list({ page, limit, status: filters.status || undefined, city: filters.city || undefined, clientId: filters.clientId || undefined, from: filters.from || undefined, to: filters.to || undefined }),
  });
  const { data: stations } = useQuery({ queryKey: ['am-stations', 'all'], queryFn: () => amStationsApi.list({ limit: 500, status: 'active' }) });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['am-rent'] }); };

  const createMut = useMutation({
    mutationFn: () => amRentApi.create(form),
    onSuccess: () => { setFormOpen(false); setForm({ stationId: 0, monthYear: firstOfMonth(), amount: 0, dueDate: firstOfMonth() }); invalidate(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => amRentApi.remove(id),
    onSuccess: () => { setDeleting(null); invalidate(); },
    onError: (e) => { setToast(extractApiError(e)); setDeleting(null); },
  });

  const columns: Column<AmRent>[] = [
    { key: 'stationName', label: 'Site', render: (r) => <span>{r.stationName ?? '—'}<br /><small style={{ color: '#888' }}>{r.city ?? ''}</small></span> },
    { key: 'ownerName', label: 'Owner (payee)', render: (r) => r.ownerName ?? '—' },
    { key: 'monthYear', label: 'Month', render: (r) => (r.monthYear ? new Date(r.monthYear).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : '—') },
    { key: 'amount', label: 'Amount', align: 'right', render: (r) => inr(r.amount) },
    { key: 'dueDate', label: 'Due', render: (r) => (r.dueDate ? new Date(r.dueDate).toLocaleDateString('en-IN') : '—') },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status} color={statusColor(r.status)} /> },
    { key: 'transactionRef', label: 'Ref', render: (r) => r.transactionRef ?? '—' },
  ];

  return (
    <>
      <PageHeader title="Rent Service" subtitle="Monthly rent paid to site owners on behalf of clients."
        action={hasPermission('am_rent.create') && (
          <Stack direction="row" spacing={1}>
            <Button startIcon={<UploadFile />} variant="outlined" onClick={() => setBulkOpen(true)}>Bulk Upload</Button>
            <Button startIcon={<Add />} variant="contained" onClick={() => { setError(null); setFormOpen(true); }}>New Rent Record</Button>
          </Stack>
        )} />

      <AmFilterBar value={filters} onChange={(f) => { setFilters(f); setPage(1); }} show={['status', 'client', 'city', 'dates']} statusOptions={['pending', 'paid', 'overdue']} />

      <DataTable
        columns={columns} rows={data?.data ?? []} loading={isLoading}
        page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id}
        actions={(r) => (
          <>
            {r.status !== 'paid' && hasPermission('am_payment.raise') && (
              <Tooltip title="Request Payment (approval)"><IconButton size="small" color="primary" onClick={() => setRequestRow(r)}><Payments fontSize="small" /></IconButton></Tooltip>
            )}
            {r.receiptUrl && <Tooltip title="Receipt"><IconButton size="small" onClick={() => window.open(r.receiptUrl!, '_blank')}><LinkIcon fontSize="small" /></IconButton></Tooltip>}
            {hasPermission('am_rent.update') && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>}
          </>
        )}
      />

      {/* New rent record */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Rent Record</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField select label="Site *" size="small" value={form.stationId || ''} onChange={(e) => setForm((f) => ({ ...f, stationId: Number(e.target.value) }))}>
              {(stations?.data ?? []).map((s) => <MenuItem key={s.id} value={s.id}>{s.stationName} — {s.city ?? ''} (owner: {s.ownerName ?? '—'})</MenuItem>)}
            </TextField>
            <TextField label="Month" type="month" size="small" InputLabelProps={{ shrink: true }} value={form.monthYear.slice(0, 7)} onChange={(e) => setForm((f) => ({ ...f, monthYear: `${e.target.value}-01` }))} />
            <TextField label="Amount (₹) *" type="number" size="small" value={form.amount || ''} onChange={(e) => setForm((f) => ({ ...f, amount: Number(e.target.value) }))} />
            <TextField label="Due Date *" type="date" size="small" InputLabelProps={{ shrink: true }} value={form.dueDate} onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))} />
            <TextField label="Notes" size="small" multiline minRows={2} value={form.notes ?? ''} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
            <Typography variant="caption" color="text.secondary">A reminder is auto-scheduled 7 days before the due date.</Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!form.stationId || !form.amount || createMut.isPending} onClick={() => { setError(null); createMut.mutate(); }}>Create</Button>
        </DialogActions>
      </Dialog>

      <RequestPaymentDialog
        open={Boolean(requestRow)} onClose={() => setRequestRow(null)}
        entityType="rent" entityId={requestRow?.id ?? null} title={`Request rent payment — ${requestRow?.stationName ?? ''}`}
        defaultAmount={requestRow?.amount} amountLocked onDone={() => { setToast('Payment requested — sent for approval.'); invalidate(); }}
      />

      <BulkImportDialog
        open={bulkOpen} onClose={() => setBulkOpen(false)} title="Bulk upload rent records" entityLabel="Rent" showCreateLogins={false}
        columns={[
          { header: 'Site', sample: 'AMS000001', required: true },
          { header: 'Month', sample: '2026-06', required: true },
          { header: 'Amount', sample: '42000', required: true },
          { header: 'Due Date', sample: '2026-06-10', required: true },
        ]}
        onImport={(rows) => amBulkApi.rent(rows)} onDone={invalidate}
      />
      <ConfirmDialog open={Boolean(deleting)} title="Delete rent record" message={`Delete the ${deleting?.monthYear ? new Date(deleting.monthYear).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : ''} rent record?`} confirmLabel="Delete"
        onCancel={() => setDeleting(null)} onConfirm={() => deleting && deleteMut.mutate(deleting.id)} />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}>
        <Alert severity="warning" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}
